// WHERE THE APPEAL STANDS, READ WITHOUT WRITING ANYTHING (WEB-FLOW-ORDER, 2026-09-29).
//
// Some calls have preconditions the server enforces and the client cannot see from the URL: `v2SearchComps` `search`
// runs ONCE per credit and answers 429 SEARCH_CAP after that (`functions/src/v2/comps/search.ts` step 3); `v2Value`
// `compute` needs a comp set (`value/value.ts`, 409 `value.compute.noComps`); a letter can only be fetched by the
// generation the server queued. A screen that asks before the precondition holds gets a refusal the owner did nothing to
// cause — which is the dead end Tyler walked into on staging, and the fake had hidden by answering everything.
//
// The server's counters and its letter pointer are on the appeal SNAPSHOT (`functions/src/v2/http/snapshot.ts`), and
// `v2Outcomes` `list` is the one call that answers snapshots and writes nothing: a pure read of the account's appeals
// through that same builder (`outcomes/list.ts`). So a screen reads the snapshot first and asks only what the server will
// answer. This is reading the server's own numbers, never deciding anything the server decides: the conditions below are
// the server's refusals, word for word.
//
// IT DOES NOT CHOOSE AN APPEAL. The id is the tab's (`./appealId.ts` says why `list` must never pick one); this finds the
// snapshot OF that id, and an id the list does not hold answers null, so the caller asks the op itself and the server
// refuses it in its own words.

import type { AppealSnapshot, V2Result } from "@contract";
import type { Transport } from "../transport/types";

/** The held appeal's snapshot off `v2Outcomes` `list`, or null when the account does not hold that id. */
export async function readSnapshot(transport: Transport, appealId: string): Promise<V2Result<AppealSnapshot | null>> {
  const listed = await transport.v2Outcomes.list();
  if (!listed.ok) return listed;
  const appeals = listed.response.body.appeals ?? [];
  return { ok: true, status: listed.status, response: appeals.find((a) => a.appealId === appealId) ?? null };
}

/** SEARCH_CAP's own condition, `used >= max`, on the server's own counters: the credit's one search has been spent, so
 *  another `search` would be refused and the saved sales are read back instead. */
export function searchSpent(snapshot: AppealSnapshot): boolean {
  const searches = snapshot.entitlement.caps.providerSearches;
  return searches.used >= searches.max;
}

/** A search has run, so `head.comps` holds a comp set and `compute`'s one precondition is met. (An edit also writes
 *  one; the snapshot cannot say so, and a caller that waits for a search asks later rather than too early.) */
export function searchHasRun(snapshot: AppealSnapshot): boolean {
  return snapshot.entitlement.caps.providerSearches.used > 0;
}
